/**
 * Blog utility functions
 *
 * Shared utilities for blog content processing, heading extraction, and ID generation.
 */

/**
 * Generate a URL-safe ID from heading text
 *
 * Converts text to lowercase, replaces spaces with dashes, removes special characters,
 * and cleans up multiple dashes and leading/trailing dashes.
 *
 * This function is used to generate IDs for headings that don't have explicit IDs,
 * ensuring consistent ID format across the blog system.
 *
 * @param text - Heading text content
 * @returns URL-safe ID string (empty string if input is empty/invalid)
 *
 * @example
 * ```typescript
 * generateHeadingId("Hello World!") // Returns: "hello-world"
 * generateHeadingId("  Multiple   Dashes  ") // Returns: "multiple-dashes"
 * generateHeadingId("Special: Characters!") // Returns: "special-characters"
 * ```
 */
export function generateHeadingId(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Content type identifiers for URL path mapping.
 */
type ContentType = 'blog' | 'guides' | 'templates';

/**
 * Mapping of content types to their parent page links.
 */
const CONTENT_TYPE_MAP: Record<ContentType, string> = {
  blog: '/blog',
  guides: '/guides',
  templates: '/templates',
} as const;

/**
 * Derives the parent page link from a URL path.
 *
 * For blog posts, guides, and templates, returns the parent listing page.
 * Otherwise returns the home page.
 *
 * Used by blog post, guide, and template layouts to determine where the
 * "Back to..." link should navigate.
 *
 * @param url - The URL path (e.g., "/blog/my-post", "/guides/topic/slug", "/templates/category/slug")
 * @returns The parent page link (e.g., "/blog", "/guides", "/templates", or "/")
 *
 * @example
 * ```typescript
 * getParentLink("/blog/my-post") // "/blog"
 * getParentLink("/guides/topic/slug") // "/guides"
 * getParentLink("/templates/category/slug") // "/templates"
 * getParentLink("/other") // "/"
 * ```
 */
export function getParentLink(url: string): string {
  // Extract the first segment of the path
  const segments = url.split('/').filter(Boolean);
  if (segments.length === 0) {
    return '/';
  }

  const firstSegment = segments[0] as ContentType;

  // Map known content types to their parent pages
  if (firstSegment in CONTENT_TYPE_MAP) {
    return CONTENT_TYPE_MAP[firstSegment];
  }

  // Default to home page for unknown paths
  return '/';
}

/**
 * Generates the back navigation label from a parent link.
 *
 * Converts a parent page link (e.g., "/blog", "/guides") into a human-readable
 * back navigation label (e.g., "Back to Blog", "Back to Guides").
 *
 * Used by blog post, guide, and template layouts to display consistent
 * back navigation labels.
 *
 * @param parentLink - The parent page link (e.g., "/blog", "/guides", "/templates", "/")
 * @returns The back label text (e.g., "Back to Blog", "Back to Guides", "Back to Home")
 *
 * @example
 * ```typescript
 * getBackLabel("/blog") // "Back to Blog"
 * getBackLabel("/guides") // "Back to Guides"
 * getBackLabel("/templates") // "Back to Templates"
 * getBackLabel("/") // "Back to Home"
 * getBackLabel("/unknown") // "Back to Home" (fallback)
 * ```
 */
export function getBackLabel(parentLink: string): string {
  const labelMap: Record<string, string> = {
    '/': 'Back to Home',
    '/blog': 'Back to Blog',
    '/guides': 'Back to Guides',
    '/templates': 'Back to Templates',
  };

  return labelMap[parentLink] || 'Back to Home';
}
